// The style brief. This is the single source of truth for what Alex wears:
// the quiz writes it, D1 stores it as one JSON blob, and the scorer prompt
// embeds it verbatim. It is imported by the React client, the Hono server, and
// (from Phase 3) the Node ingestion script — keep it environment-agnostic.
//
// Evolving the shape: bump PROFILE_VERSION and handle the older version in
// `parseProfile`. A blob that no longer validates reads back as "no profile",
// which re-runs the quiz rather than scoring against a corrupt brief.

import { z } from 'zod';

export const PROFILE_VERSION = 1;

export const CHEST_SIZES = ['S', 'M', 'L', 'XL'] as const;
export const FITS = ['slim', 'regular', 'relaxed'] as const;
export const PALETTES = ['earth', 'cool', 'monochrome', 'bold'] as const;
export const PATTERN_TOLERANCES = ['solids', 'subtle', 'loud'] as const;
export const VIBES = [
  'camp-collar-summer',
  'workwear',
  'ivy-prep',
  'minimalist',
  'western',
  'outdoor-gorp',
  'streetwear',
  'retro-70s',
] as const;

// Labels live next to the values they describe so a renamed vibe can't leave a
// stale string in the UI. Blurbs are what the quiz shows under each chip.
export const FIT_LABELS: Record<(typeof FITS)[number], string> = {
  slim: 'Slim',
  regular: 'Regular',
  relaxed: 'Relaxed',
};

export const PALETTE_LABELS: Record<(typeof PALETTES)[number], string> = {
  earth: 'Earth — olive, rust, sand, brown',
  cool: 'Cool — navy, grey, slate, white',
  monochrome: 'Monochrome — black, white, grey',
  bold: 'Bold — saturated colour, contrast',
};

export const PATTERN_LABELS: Record<(typeof PATTERN_TOLERANCES)[number], string> = {
  solids: 'Solids only',
  subtle: 'Subtle — stripes, checks, texture',
  loud: 'Loud — prints, florals, big graphics',
};

export const VIBE_LABELS: Record<(typeof VIBES)[number], string> = {
  'camp-collar-summer': 'Camp collar',
  workwear: 'Workwear',
  'ivy-prep': 'Ivy / prep',
  minimalist: 'Minimalist',
  western: 'Western',
  'outdoor-gorp': 'Outdoor / gorp',
  streetwear: 'Streetwear',
  'retro-70s': 'Retro 70s',
};

// 1–5, dressed-down to dressed-up. The scorer reads the range, not the labels.
export const FORMALITY_LABELS: Record<number, string> = {
  1: 'Lounge',
  2: 'Weekend',
  3: 'Smart casual',
  4: 'Business casual',
  5: 'Tailored',
};

const sizesSchema = z.object({
  // Nullable because plenty of people don't know their neck or waist by
  // number, and a guessed measurement is worse than an absent one.
  neck: z.number().min(12).max(22).nullable(),
  chest: z.enum(CHEST_SIZES),
  waist: z.number().int().min(24).max(50).nullable(),
});

const formalitySchema = z
  .object({
    min: z.number().int().min(1).max(5),
    max: z.number().int().min(1).max(5),
  })
  .refine((f) => f.min <= f.max, { message: 'formality.min must be <= formality.max' });

// The tiered price rule, as data. The scorer prompt turns these three numbers
// into its rubric, so changing them here changes how deals are scored.
const budgetSchema = z
  .object({
    valueMaxUsd: z.number().int().min(1).max(1000),
    midMaxUsd: z.number().int().min(1).max(5000),
    midOnlyIfDiscountPct: z.number().int().min(0).max(90),
  })
  .refine((b) => b.valueMaxUsd <= b.midMaxUsd, {
    message: 'budget.valueMaxUsd must be <= budget.midMaxUsd',
  });

export const profileSchema = z.object({
  version: z.literal(PROFILE_VERSION),
  sizes: sizesSchema,
  fit: z.enum(FITS),
  palette: z.enum(PALETTES),
  patternTolerance: z.enum(PATTERN_TOLERANCES),
  formality: formalitySchema,
  budget: budgetSchema,
  brands: z.array(z.string().trim().min(1).max(60)).max(30),
  vibes: z.array(z.enum(VIBES)).max(VIBES.length),
  // Hidden: the Save/Dismiss feedback loop folds terms in here (backlog job).
  // Split by polarity because "more like this" and "less like this" pull the
  // scorer in opposite directions and must not collapse into one list.
  keywords: z.object({
    more: z.array(z.string().trim().min(1).max(60)).max(100),
    less: z.array(z.string().trim().min(1).max(60)).max(100),
  }),
});

export type Profile = z.infer<typeof profileSchema>;
export type ChestSize = (typeof CHEST_SIZES)[number];
export type Fit = (typeof FITS)[number];
export type Palette = (typeof PALETTES)[number];
export type PatternTolerance = (typeof PATTERN_TOLERANCES)[number];
export type Vibe = (typeof VIBES)[number];

// What an untouched quiz starts from. The budget tiers are the plan's locked
// defaults; everything else is the least-opinionated option so an unanswered
// control doesn't quietly assert a taste the user never expressed.
export function defaultProfile(): Profile {
  return {
    version: PROFILE_VERSION,
    sizes: { neck: null, chest: 'M', waist: null },
    fit: 'regular',
    palette: 'earth',
    patternTolerance: 'subtle',
    formality: { min: 1, max: 3 },
    budget: { valueMaxUsd: 50, midMaxUsd: 150, midOnlyIfDiscountPct: 40 },
    brands: [],
    vibes: [],
    keywords: { more: [], less: [] },
  };
}

export type ParseResult =
  | { ok: true; profile: Profile }
  | { ok: false; error: string };

export function parseProfile(input: unknown): ParseResult {
  const result = profileSchema.safeParse(input);
  if (result.success) return { ok: true, profile: result.data };
  const first = result.error.issues[0];
  const path = first?.path.join('.');
  return {
    ok: false,
    error: path ? `${path}: ${first.message}` : (first?.message ?? 'invalid profile'),
  };
}

// Comma-separated free text <-> the stored list. Shared so the quiz input and
// any future importer agree on what counts as a separator.
export function parseBrands(input: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of input.split(',')) {
    const brand = raw.trim().slice(0, 60);
    const key = brand.toLowerCase();
    if (!brand || seen.has(key)) continue;
    seen.add(key);
    out.push(brand);
    if (out.length === 30) break;
  }
  return out;
}

export function formatBrands(brands: string[]): string {
  return brands.join(', ');
}
